package or.garden.manager.features.seeds.components

import androidx.compose.runtime.*
import kotlinx.browser.window
import or.garden.manager.domain.model.SeedData
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.*

@Composable
fun SeedEditView(
    selectedSeed: SeedData?,
    seedName: String,
    onSeedNameChange: (String) -> Unit,
    seedVariety: String,
    onSeedVarietyChange: (String) -> Unit,
    seedType: String,
    onSeedTypeChange: (String) -> Unit,
    seedHarvestTime: String,
    onSeedHarvestTimeChange: (String) -> Unit,
    seedSelectedMonths: Set<Int>,
    onSeedSelectedMonthsChange: (Set<Int>) -> Unit,
    seedIsHybrid: Boolean,
    onSeedIsHybridChange: (Boolean) -> Unit,
    seedPhotoPreview: String?,
    onPhotoSelected: (ByteArray, String) -> Unit,
    seedBackPhotoPreview: String?,
    onBackPhotoSelected: (ByteArray, String) -> Unit,
    isSaving: Boolean,
    onSave: () -> Unit,
    onBack: () -> Unit
) {
    Div({ classes("max-w-4xl", "mx-auto") }) {
        // Header
        Div({ classes("flex", "items-center", "justify-between", "mb-8") }) {
            Div({ classes("flex", "items-center", "gap-4") }) {
                Button({
                    classes(
                        "w-10", "h-10", "rounded-xl", "bg-white", "border", "border-gray-200", "flex", "items-center", "justify-center", "text-gray-600", "hover:bg-emerald-50", "hover:text-emerald-600", "hover:border-emerald-100", "transition-all", "shadow-sm"
                    )
                    onClick { onBack() }
                }) {
                    I({ classes("fas", "fa-arrow-left") })
                }
                H2({ classes("text-3xl", "font-bold", "text-emerald-900") }) {
                    Text(if (selectedSeed == null) "Nova sjemenka" else "Uredi sjemenku")
                }
            }

            Button({
                val btnClasses = mutableListOf(
                    "px-6", "py-2", "bg-emerald-600", "text-white", "rounded-xl", "font-bold", "flex", "items-center", "gap-2", "transition-all"
                )
                if (isSaving) btnClasses.add("opacity-50")
                classes(btnClasses)
                onClick { onSave() }
                if (isSaving) disabled()
            }) {
                if (isSaving) I({ classes("fas", "fa-circle-notch", "fa-spin") })
                else I({ classes("fas", "fa-save") })
                Text(if (isSaving) "Spremanje..." else "Sačuvaj")
            }
        }

        Div({
            classes("bg-white", "rounded-3xl", "p-8", "shadow-sm", "border", "border-gray-100")
        }) {
            Div({ classes("space-y-6") }) {
                // Photo Upload Row
                Div({ classes("flex", "gap-4", "justify-center", "mb-6") }) {
                    SeedPhotoPicker(
                        label = "Prednja strana",
                        previewUrl = seedPhotoPreview,
                        onFileSelected = { bytes, url -> onPhotoSelected(bytes, url) }
                    )
                    SeedPhotoPicker(
                        label = "Zadnja strana",
                        previewUrl = seedBackPhotoPreview,
                        onFileSelected = { bytes, url -> onBackPhotoSelected(bytes, url) }
                    )
                }

                Div {
                    Label(attrs = {
                        classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1")
                    }) { Text("Naziv sjemena") }
                    Input(InputType.Text) {
                        classes(
                            "w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none"
                        )
                        value(seedName)
                        onInput { onSeedNameChange(it.value) }
                        placeholder("npr. Paradajz Volovsko Srce")
                    }
                }

                Div {
                    Label(attrs = {
                        classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1")
                    }) { Text("Sorta (Latinski naziv)") }
                    Input(InputType.Text) {
                        classes(
                            "w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none"
                        )
                        value(seedVariety)
                        onInput { onSeedVarietyChange(it.value) }
                        placeholder("npr. Solanum lycopersicum")
                    }
                }

                Div({ classes("grid", "grid-cols-2", "gap-4") }) {
                    Div {
                        Label(attrs = {
                            classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1")
                        }) { Text("Tip") }
                        Select({
                            classes(
                                "w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none"
                            )
                            onInput { onSeedTypeChange(it.value ?: "early") }
                        }) {
                            Option("early", attrs = { if (seedType == "early") selected() }) { Text("Rana") }
                            Option("medium", attrs = { if (seedType == "medium") selected() }) { Text("Srednja") }
                            Option("late", attrs = { if (seedType == "late") selected() }) { Text("Kasna") }
                        }
                    }
                    Div {
                        Label(attrs = {
                            classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1")
                        }) { Text("Vrijeme berbe (dana)") }
                        Input(InputType.Number) {
                            classes(
                                "w-full", "p-3", "border", "border-gray-200", "rounded-xl", "focus:ring-2", "focus:ring-emerald-500", "outline-none"
                            )
                            value(seedHarvestTime)
                            onInput { onSeedHarvestTimeChange(it.value.toString()) }
                        }
                    }
                }

                Div {
                    Label(attrs = {
                        classes("block", "text-sm", "font-medium", "text-gray-700", "mb-1")
                    }) { Text("Mjeseci za rasad") }
                    Div({ classes("flex", "flex-wrap", "gap-2") }) {
                        val months = listOf("Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Avg", "Sep", "Okt", "Nov", "Dec")
                        months.forEachIndexed { index, month ->
                            val monthNum = index + 1
                            val isSelected = seedSelectedMonths.contains(monthNum)
                            Div({
                                classes("px-3", "py-2", "rounded-xl", "text-xs", "font-bold", "cursor-pointer", "transition-all")
                                if (isSelected) {
                                    classes("bg-emerald-500", "text-white")
                                } else {
                                    classes("bg-gray-100", "text-gray-600", "hover:bg-gray-200")
                                }
                                onClick {
                                    onSeedSelectedMonthsChange(
                                        if (isSelected) seedSelectedMonths - monthNum else seedSelectedMonths + monthNum
                                    )
                                }
                            }) { Text(month) }
                        }
                    }
                }

                Div({
                    classes("flex", "items-center", "gap-3", "p-4", "bg-gray-50", "rounded-2xl")
                }) {
                    Input(InputType.Checkbox) {
                        id("seed-hybrid")
                        checked(seedIsHybrid)
                        onInput { onSeedIsHybridChange(it.value) }
                    }
                    Label(forId = "seed-hybrid", attrs = {
                        classes("text-sm", "font-bold", "text-gray-700", "cursor-pointer")
                    }) { Text("Hibridna sorta (F1)") }
                }
            }
        }
    }
}

@Composable
fun SeedPhotoPicker(
    label: String,
    previewUrl: String?,
    onFileSelected: (ByteArray, String) -> Unit
) {
    Div({ classes("flex", "flex-col", "items-center", "gap-2") }) {
        Label(attrs = { classes("text-xs", "font-bold", "text-gray-400", "uppercase") }) { Text(label) }
        Div({
            classes(
                "w-32", "h-44", "bg-gray-50", "border-2", "border-dashed", "border-gray-200", "rounded-2xl", "flex", "items-center", "justify-center", "cursor-pointer", "relative", "overflow-hidden", "hover:border-emerald-500", "transition-colors"
            )
            onClick {
                window.document.getElementById("file-input-$label")?.asDynamic().click()
            }
        }) {
            if (previewUrl != null) {
                Img(src = previewUrl, attrs = { classes("w-full", "h-full", "object-cover") })
            } else {
                Div({ classes("text-center", "p-4") }) {
                    I({ classes("fas", "fa-camera", "text-gray-300", "text-2xl", "mb-2", "block") })
                    Span({
                        classes("text-[10px]", "text-gray-400", "font-medium")
                    }) { Text("Klikni za sliku") }
                }
            }

            Input(InputType.File) {
                id("file-input-$label")
                classes("hidden")
                onInput { event ->
                    val file = event.target.asDynamic().files[0]
                    if (file != null) {
                        val reader = org.w3c.files.FileReader()
                        reader.onload = { loadEvent ->
                            val result = loadEvent.target.asDynamic().result
                            if (result != null) {
                                val uint8Array = org.khronos.webgl.Uint8Array(result as org.khronos.webgl.ArrayBuffer)
                                val len = uint8Array.length
                                val kotlinBytes = ByteArray(len)
                                val dynamicArray = uint8Array.asDynamic()
                                for (i in 0 until len) {
                                    kotlinBytes[i] = dynamicArray[i] as Byte
                                }

                                val url = window.asDynamic().URL.createObjectURL(file) as String
                                onFileSelected(kotlinBytes, url)
                            }
                        }
                        reader.readAsArrayBuffer(file)
                    }
                }
            }
        }
    }
}
